import { useEffect, useState } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import { supabase } from "../lib/supabase"
import { ArrowLeft, Building2, Presentation, Calendar } from "lucide-react"

/*
  SPEAKER BIO PAGE
  ----------------
  Displays detailed information about a specific speaker
  including photo, bio, organization, speaking topic, and type
*/

export default function SpeakerBio() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [speaker, setSpeaker] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadSpeaker()
  }, [id])

  async function loadSpeaker() {
    const { data, error } = await supabase
      .from("speakers")
      .select("*")
      .eq("id", id)
      .eq("status", "confirmed")
      .single()

    if (error) {
      console.error("Error loading speaker:", error)
      setLoading(false)
      return
    }

    setSpeaker(data)
    setLoading(false)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-amber-400 border-t-transparent"></div>
          <p className="mt-4 text-slate-300">Loading speaker details...</p>
        </div>
      </div>
    )
  }

  if (!speaker) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h1 className="text-3xl font-bold mb-4">Speaker Not Found</h1>
          <p className="text-slate-300 mb-8">
            The speaker you're looking for doesn't exist or is not available.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold px-6 py-3 rounded-lg transition"
          >
            <ArrowLeft size={20} />
            Back to Home
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 mb-8 transition"
        >
          <ArrowLeft size={20} />
          Back to Speakers
        </button>

        {/* Speaker Header */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-3xl p-8 md:p-12 shadow-2xl ring-1 ring-amber-400/20">
          <div className="grid md:grid-cols-[300px_1fr] gap-8 items-start">
            {/* Photo */}
            <div className="mx-auto md:mx-0">
              {speaker.photo_url ? (
                <img
                  src={speaker.photo_url}
                  alt={speaker.full_name}
                  className="w-64 h-64 rounded-2xl object-cover shadow-lg ring-4 ring-amber-400/30"
                />
              ) : (
                <div className="w-64 h-64 rounded-2xl bg-amber-500/20 flex items-center justify-center text-amber-400 font-bold text-6xl shadow-lg">
                  {speaker.full_name.charAt(0)}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="space-y-6">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold mb-2">
                  {speaker.full_name}
                </h1>
                
                {speaker.organization && (
                  <div className="flex items-center gap-2 text-amber-400 text-xl mb-4">
                    <Building2 size={20} />
                    <span>{speaker.organization}</span>
                  </div>
                )}

                <div className="flex flex-wrap gap-3">
                  {speaker.speaking_type && (
                    <span className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-300 px-4 py-2 rounded-full text-sm font-semibold">
                      <Presentation size={16} />
                      {speaker.speaking_type}
                    </span>
                  )}
                  
                  <span className="inline-flex items-center gap-2 bg-slate-700/50 text-slate-300 px-4 py-2 rounded-full text-sm">
                    <Calendar size={16} />
                    16-17 Jan 2026
                  </span>
                </div>
              </div>

              {/* Speaking Topic */}
              {speaker.speaking_topic && (
                <div className="bg-slate-900/50 rounded-xl p-6 border border-amber-400/20">
                  <h2 className="text-sm uppercase tracking-wide text-amber-400 mb-2 font-semibold">
                    Speaking Topic
                  </h2>
                  <p className="text-xl text-slate-100">
                    {speaker.speaking_topic}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Bio Section */}
          {speaker.bio && (
            <div className="mt-12 pt-8 border-t border-slate-700/50">
              <h2 className="text-2xl font-bold mb-4 text-amber-400">
                Biography
              </h2>
              <div className="prose prose-invert prose-lg max-w-none">
                <p className="text-slate-200 leading-relaxed whitespace-pre-line">
                  {speaker.bio}
                </p>
              </div>
            </div>
          )}

          {/* Call to Action */}
          <div className="mt-12 pt-8 border-t border-slate-700/50">
            <div className="bg-gradient-to-r from-amber-500/10 to-amber-600/10 rounded-xl p-6 text-center border border-amber-400/20">
              <h3 className="text-xl font-semibold mb-2">
                Don't Miss This Session
              </h3>
              <p className="text-slate-300 mb-4">
                Register now to attend {speaker.full_name}'s presentation at Africamed Business Forum 2026
              </p>
              <Link
                to="/register"
                className="inline-block bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-8 py-3 rounded-lg transition shadow-lg"
              >
                Register Now
              </Link>
            </div>
          </div>
        </div>

        {/* Other Speakers Section */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Other Featured Speakers</h2>
          <div className="text-center">
            <Link
              to="/#speakers"
              className="inline-block text-amber-400 hover:text-amber-300 font-semibold transition"
            >
              View All Speakers →
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
